import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Play, Pause, Mic, MicOff, Keyboard, Volume2, RotateCcw, Check } from 'lucide-react';
import { Button } from './ui/button';
import { configureEnglishRecognition, getSpeechRecognitionConstructor, getSttAvailability, getSttErrorMessage } from '../utils/speechRecognition';

/**
 * useAvailableVoices — small React hook exposing the current list of
 * browser TTS voices. Voices load asynchronously in most browsers.
 */
export const useAvailableVoices = () => {
  const [voices, setVoices] = useState([]);
  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const load = () => {
      const list = window.speechSynthesis.getVoices();
      if (list && list.length) setVoices(list);
    };
    load();
    window.speechSynthesis.addEventListener?.('voiceschanged', load);
    return () => { window.speechSynthesis.removeEventListener?.('voiceschanged', load); };
  }, []);
  return voices;
};

// Best-effort gender detection from voice name (Web Speech API does not expose gender).
const inferGender = (voice) => {
  const n = (voice.name || '').toLowerCase();
  const femaleHints = ['female', 'zira', 'samantha', 'karen', 'susan', 'victoria', 'kate', 'moira', 'tessa', 'fiona', 'aria', 'jenny', 'nova', 'ava', 'emma', 'amy', 'anna', 'sara', 'ivy', 'joanna', 'salli'];
  const maleHints   = ['male', 'david', 'mark', 'alex', 'daniel', 'fred', 'george', 'james', 'thomas', 'diego', 'oliver', 'brian', 'matthew', 'guy', 'ryan', 'ivan'];
  if (femaleHints.some((h) => n.includes(h))) return 'female';
  if (maleHints.some((h) => n.includes(h))) return 'male';
  return 'unknown';
};

/**
 * VoicePicker — dropdown to let the client pick which TTS voice reads
 * the questions. Groups voices by gender (best-effort) and shows both
 * male and female options across all available languages.
 */
export const VoicePicker = ({ selectedVoiceURI, onChange }) => {
  const voices = useAvailableVoices();

  // Sort: English voices first, then others, alphabetical within each group
  const sorted = useMemo(() => {
    const arr = [...voices];
    arr.sort((a, b) => {
      const aEn = (a.lang || '').toLowerCase().startsWith('en') ? 0 : 1;
      const bEn = (b.lang || '').toLowerCase().startsWith('en') ? 0 : 1;
      if (aEn !== bEn) return aEn - bEn;
      return (a.name || '').localeCompare(b.name || '');
    });
    return arr;
  }, [voices]);

  const female = sorted.filter((v) => inferGender(v) === 'female');
  const male   = sorted.filter((v) => inferGender(v) === 'male');
  const other  = sorted.filter((v) => inferGender(v) === 'unknown');

  return (
    <div className="flex items-center gap-2" data-testid="voice-picker">
      <Volume2 className="w-4 h-4 text-gray-500" />
      <select
        value={selectedVoiceURI || ''}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm border rounded px-2 py-1 bg-white max-w-[260px]"
        data-testid="voice-picker-select"
      >
        <option value="">Default voice</option>
        {female.length > 0 && (
          <optgroup label="Female voices">
            {female.map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>
            ))}
          </optgroup>
        )}
        {male.length > 0 && (
          <optgroup label="Male voices">
            {male.map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>
            ))}
          </optgroup>
        )}
        {other.length > 0 && (
          <optgroup label="Other voices">
            {other.map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>
            ))}
          </optgroup>
        )}
      </select>
    </div>
  );
};

/**
 * useSpeak — small TTS hook usable outside the VoiceQuestion card.
 * Returns { isPlaying, play, stop, toggle } for a given text + voice URI.
 */
export const useSpeak = (selectedVoiceURI, rate = 1.0) => {
  const voices = useAvailableVoices();
  const [isPlaying, setIsPlaying] = useState(false);
  const utterRef = useRef(null);
  const keepAliveRef = useRef(null);
  const onEndCallbackRef = useRef(null);
  // Guard so a `stop()` while mid-narration doesn't get overridden by a queued
  // `speakNext` (cancel() fires onend which used to advance to the next chunk).
  const stoppedRef = useRef(false);
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const stop = () => {
    stoppedRef.current = true;
    // Detach the end-callback so cancel()'s synthetic onend doesn't re-enter speakNext.
    if (utterRef.current) {
      try {
        utterRef.current.onend = null;
        utterRef.current.onerror = null;
      } catch (_) { /* ignore */ }
    }
    if (supported) window.speechSynthesis.cancel();
    if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
    onEndCallbackRef.current = null;
    setIsPlaying(false);
  };

  const play = (text, opts = {}) => {
    const onEnd = opts.onEnd;
    if (!supported || !text) { onEnd?.(); return; }
    onEndCallbackRef.current = onEnd || null;
    stoppedRef.current = false;
    try {
      window.speechSynthesis.cancel();
      window.speechSynthesis.resume();
      const chunks = String(text)
        .match(/[^.!?\n]+[.!?\n]?/g)
        ?.map((s) => s.trim()).filter(Boolean) || [String(text)];
      let idx = 0;
      const speakNext = () => {
        // Bail out if the user pressed Stop, or all chunks are done.
        if (stoppedRef.current || idx >= chunks.length) {
          setIsPlaying(false);
          if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
          if (!stoppedRef.current && onEndCallbackRef.current) {
            const cb = onEndCallbackRef.current;
            onEndCallbackRef.current = null;
            cb();
          }
          return;
        }
        const u = new SpeechSynthesisUtterance(chunks[idx]);
        if (selectedVoiceURI) {
          const v = voices.find((x) => x.voiceURI === selectedVoiceURI);
          if (v) u.voice = v;
        }
        u.rate = Math.max(0.5, Math.min(2, opts.rate ?? rate ?? 1.0));
        u.pitch = 1.0;
        u.onstart = () => { if (!stoppedRef.current) setIsPlaying(true); };
        u.onend = () => { if (stoppedRef.current) return; idx += 1; speakNext(); };
        u.onerror = () => { if (stoppedRef.current) return; idx += 1; speakNext(); };
        utterRef.current = u;
        window.speechSynthesis.speak(u);
      };
      speakNext();
      if (keepAliveRef.current) clearInterval(keepAliveRef.current);
      keepAliveRef.current = setInterval(() => {
        try {
          if (stoppedRef.current) return;
          if (window.speechSynthesis.paused) window.speechSynthesis.resume();
        } catch (e) { /* ignore */ }
      }, 10000);
    } catch (e) { setIsPlaying(false); onEnd?.(); }
  };

  const toggle = (text, opts) => (isPlaying ? stop() : play(text, opts));

  useEffect(() => () => stop(), []);

  return { isPlaying, play, stop, toggle, supported };
};

/**
 * NarrateButton — a small TTS play/stop button for reading arbitrary
 * text (e.g. a moral story or a scenario question) via browser TTS.
 */
export const NarrateButton = ({ text, label = 'Narrate', selectedVoiceURI, className = '', testId }) => {
  const [rate, setRate] = useState(() => {
    try {
      const stored = window.localStorage.getItem('narrate_rate');
      return stored ? parseFloat(stored) : 1.0;
    } catch (e) { return 1.0; }
  });
  const { isPlaying, play, stop, supported } = useSpeak(selectedVoiceURI, rate);

  const handleRateChange = (newRate) => {
    setRate(newRate);
    try { window.localStorage.setItem('narrate_rate', String(newRate)); } catch (e) { /* ignore */ }
    if (isPlaying) {
      // Restart at new speed so change takes effect immediately.
      stop();
      setTimeout(() => play(text, { rate: newRate }), 100);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => (isPlaying ? stop() : play(text, { rate }))}
        disabled={!supported || !text}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border transition-colors ${
          isPlaying
            ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
            : 'bg-indigo-50 border-indigo-300 text-indigo-700 hover:bg-indigo-100'
        } disabled:opacity-40 ${className}`}
        data-testid={testId || 'narrate-btn'}
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        {isPlaying ? 'Stop narration' : label}
      </button>
      <select
        value={rate}
        onChange={(e) => handleRateChange(parseFloat(e.target.value))}
        className="text-xs border rounded px-1.5 py-1 bg-white"
        aria-label="Narration speed"
        data-testid={(testId || 'narrate-btn') + '-speed'}
        title="Narration speed"
      >
        <option value={0.5}>0.5×</option>
        <option value={0.75}>0.75×</option>
        <option value={1.0}>1×</option>
        <option value={1.25}>1.25×</option>
        <option value={1.5}>1.5×</option>
        <option value={2.0}>2×</option>
      </select>
    </div>
  );
};

/**
 * autoPunctuate — lightweight rules pass that improves the readability of raw
 * speech-to-text output before it gets appended to a text field. Applies:
 *   • Collapse consecutive whitespace to a single space
 *   • Capitalize the first letter of the sentence
 *   • Capitalize after '.', '!', '?' terminators
 *   • Capitalize the pronoun 'i'
 *   • Insert a comma before common conjunctions when they appear mid-sentence
 *     without existing punctuation ('and', 'but', 'because', 'so', 'then')
 *   • Insert '?' at the end if the phrase starts with an interrogative and
 *     lacks terminal punctuation; otherwise insert '.'
 *   • Fix stray whitespace before punctuation ('word .' -> 'word.')
 * Idempotent — calling it twice yields the same output.
 */
export const autoPunctuate = (raw) => {
  if (!raw || typeof raw !== 'string') return raw || '';
  let s = raw.replace(/\s+/g, ' ').trim();
  if (!s) return '';
  // Space-before-punctuation cleanup ("word ." -> "word.")
  s = s.replace(/\s+([.,!?;:])/g, '$1');
  // Insert comma before common conjunctions when they appear mid-sentence
  const conjunctions = ['and', 'but', 'because', 'so then', 'then', 'or', 'however'];
  for (const w of conjunctions) {
    const re = new RegExp(`([a-zA-Z0-9])\\s+${w}\\s+`, 'gi');
    s = s.replace(re, (m, prev) => `${prev}, ${w} `);
  }
  // Capitalize 'i' as a standalone pronoun
  s = s.replace(/(^|[^a-zA-Z])i(?=[^a-zA-Z]|$)/g, (m, prev) => `${prev}I`);
  // Capitalize first letter
  s = s.charAt(0).toUpperCase() + s.slice(1);
  // Capitalize the character following '. ', '! ', '? '
  s = s.replace(/([.!?]\s+)([a-z])/g, (m, tail, ch) => `${tail}${ch.toUpperCase()}`);
  // Ensure terminal punctuation
  if (!/[.!?]$/.test(s)) {
    const interrogative = /^(who|what|when|where|why|how|is|are|do|does|did|can|could|would|should|will)\b/i.test(s);
    s = s + (interrogative ? '?' : '.');
  }
  return s;
};


/**
 * MicInput — compact inline mic button that appends STT results to a text
 * value via `onAppend(newText)`. Intended to sit next to a textarea.
 */
export const MicInput = ({ onAppend, showListeningIndicator = false, className = '', testId }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);
  const startingRef = useRef(false);
  const availability = getSttAvailability();
  const supported = availability.available;

  const stop = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
    }
    recognitionRef.current = null;
    startingRef.current = false;
    setIsRecording(false);
  };

  const start = () => {
    if (!supported) { setError(availability.message); return; }
    if (startingRef.current || recognitionRef.current) return;
    startingRef.current = true;
    try {
      const SpeechRecognition = getSpeechRecognitionConstructor();
      const rec = new SpeechRecognition();
      configureEnglishRecognition(rec);
      rec.onstart = () => { setIsRecording(true); setError(''); };
      rec.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript;
        }
        if (finalTranscript) onAppend(autoPunctuate(finalTranscript.trim()));
      };
      rec.onerror = (event) => { setError(getSttErrorMessage(event?.error)); recognitionRef.current = null; startingRef.current = false; setIsRecording(false); };
      rec.onend = () => { recognitionRef.current = null; startingRef.current = false; setIsRecording(false); };
      recognitionRef.current = rec;
      rec.start();
    } catch (e) { recognitionRef.current = null; startingRef.current = false; setError(getSttErrorMessage()); setIsRecording(false); }
  };

  useEffect(() => () => stop(), []);

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => (isRecording ? stop() : start())}
        disabled={false}
        title={supported ? (isRecording ? 'Stop recording' : 'Speak your answer') : 'Voice input not supported'}
        className={`inline-flex items-center justify-center w-9 h-9 rounded-md border transition-colors ${
          isRecording
            ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100 animate-pulse'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        } disabled:opacity-40 ${className}`}
        data-testid={testId || 'mic-input-btn'}
      >
        {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </button>
      {showListeningIndicator && isRecording && <ListeningIndicator />}
      {error && <span className="text-xs text-amber-700" role="status">{error}</span>}
    </div>
  );
};

/**
 * ListeningIndicator — small animated visual cue shown while STT is
 * actively listening. Combines a pulsing dot, three EQ-style bars, and
 * a "Listening…" label so the client has clear confirmation the mic
 * is on and their words are being captured.
 */
export const ListeningIndicator = ({ label = 'Listening…' }) => (
  <div
    className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-50 border border-red-200 text-red-700"
    role="status"
    aria-live="polite"
    data-testid="listening-indicator"
  >
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
    </span>
    <span className="flex items-end gap-0.5 h-3">
      <span className="w-0.5 bg-red-500 rounded-sm animate-listening-bar-1" style={{ height: '30%' }} />
      <span className="w-0.5 bg-red-500 rounded-sm animate-listening-bar-2" style={{ height: '60%' }} />
      <span className="w-0.5 bg-red-500 rounded-sm animate-listening-bar-3" style={{ height: '80%' }} />
      <span className="w-0.5 bg-red-500 rounded-sm animate-listening-bar-2" style={{ height: '50%' }} />
      <span className="w-0.5 bg-red-500 rounded-sm animate-listening-bar-1" style={{ height: '70%' }} />
    </span>
    <span className="text-xs font-medium">{label}</span>
    <style>{`
      @keyframes listening-bar-1 { 0%,100% { transform: scaleY(0.4);} 50% { transform: scaleY(1);} }
      @keyframes listening-bar-2 { 0%,100% { transform: scaleY(0.7);} 50% { transform: scaleY(0.3);} }
      @keyframes listening-bar-3 { 0%,100% { transform: scaleY(0.5);} 50% { transform: scaleY(1);} }
      .animate-listening-bar-1 { animation: listening-bar-1 0.9s ease-in-out infinite; transform-origin: bottom; }
      .animate-listening-bar-2 { animation: listening-bar-2 0.9s ease-in-out infinite 0.15s; transform-origin: bottom; }
      .animate-listening-bar-3 { animation: listening-bar-3 0.9s ease-in-out infinite 0.3s; transform-origin: bottom; }
    `}</style>
  </div>
);

/**
 * VoiceQuestion — a single voice-first Q&A card.
 *
 * Props:
 *   question           string  — the prompt read out loud
 *   value              string  — current answer (managed by parent)
 *   onChange(newValue)         — commit an answer edit
 *   onConfirm()               — user pressed "Confirm & Next"
 *   onSkip()                  — optional Skip handler
 *   selectedVoiceURI  string  — currently chosen TTS voice
 *   allowType         bool    — show the "Type instead" button (default true)
 *   placeholder       string  — placeholder text for the typed textarea
 */
const VoiceQuestion = ({
  question,
  helperText,
  value,
  onChange,
  onConfirm,
  onSkip,
  selectedVoiceURI,
  allowType = true,
  autoPlay = false,
  placeholder = 'Your answer will appear here…',
  testId,
  displayValue,  // Optional: overrides transcript rendering (e.g. DOB shown DD-MM-YYYY while internal `value` stays ISO)
}) => {
  const voices = useAvailableVoices();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [typeMode, setTypeMode] = useState(false);
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);
  const interimRef = useRef('');
  // Seed value captured at startRecording — kept separate from interimRef
  // so onresult can rebuild the transcript idempotently on every event.
  const interimSeedRef = useRef('');
  const autoPlayedRef = useRef(false);
  // Set to true while the CURRENT utterance is one we started via auto-play,
  // so we know to auto-start the mic when it ends (but NOT when the user
  // manually re-plays the question).
  const autoPlayInFlightRef = useRef(false);
  // Refs mirroring live state so the TTS `u.onend` closure (created when the
  // utterance was scheduled) can decide auto-record against the CURRENT
  // typeMode / isRecording instead of stale values (e.g. if the client
  // switched to Type Instead while the question was still narrating).
  const typeModeRef = useRef(typeMode);
  useEffect(() => { typeModeRef.current = typeMode; }, [typeMode]);
  const isRecordingRef = useRef(isRecording);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);

  // --- Feature detection ---
  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const availability = getSttAvailability();
  const sttSupported = availability.available;

  // --- TTS ---
  const playQuestion = () => {
    if (!ttsSupported || !question) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(question);
      if (selectedVoiceURI) {
        const v = voices.find((x) => x.voiceURI === selectedVoiceURI);
        if (v) u.voice = v;
      }
      u.rate = 1.0;
      u.pitch = 1.0;
      u.onstart = () => setIsPlaying(true);
      u.onend = () => {
        setIsPlaying(false);
        // Auto-start the mic AFTER the narration finishes so the client can
        // just answer — same conversational feel as Phase 1's design ask.
        // We only auto-record if:
        //  - this utterance was the auto-played one (not a manual replay),
        //  - STT is supported,
        //  - the client hasn't switched to type mode (read via ref so a
        //    toggle DURING narration is respected),
        //  - the mic isn't already recording.
        // NOTE: we deliberately DO NOT skip when `value` is already truthy —
        // some Phase 1 fields default to a numeric 0 which is not a real
        // answer, and even for a draft-restored answer the client should
        // still be able to speak a new one after the question is re-read.
        autoPlayInFlightRef.current = false;
        // Recognition deliberately does not start from narration callbacks.
        // Every microphone session requires an explicit button gesture.
      };
      u.onerror = () => { setIsPlaying(false); autoPlayInFlightRef.current = false; };
      window.speechSynthesis.speak(u);
    } catch (e) {
      setError('Unable to play the question.');
      autoPlayInFlightRef.current = false;
    }
  };
  const stopPlayback = () => {
    if (ttsSupported) window.speechSynthesis.cancel();
    setIsPlaying(false);
  };

  // --- STT ---
  const startRecording = () => {
    if (!sttSupported) {
      setError(availability.message);
      setTypeMode(true);
      return;
    }
    if (isRecordingRef.current || recognitionRef.current) return;
    try {
      const SpeechRecognition = getSpeechRecognitionConstructor();
      const rec = new SpeechRecognition();
      // Continuous:true so Chrome collects long statements ("I have three
      // children and two brothers") as multiple final chunks without
      // cutting off mid-sentence. We stop the mic on `onspeechend` — the
      // browser's built-in silence detector — so recording ends the moment
      // the client stops talking, but not before. This gives us:
      //   • long-statement support (no cut-off)
      //   • short-word support (interim results included in onChange)
      //   • no post-speech duplication (mic stops when client stops)
      configureEnglishRecognition(rec);
      interimSeedRef.current = (value || '').trim();
      interimRef.current = '';
      let userStoppedRef = false;
      rec.onstart = () => { setIsRecording(true); setError(''); userStoppedRef = false; };
      rec.onspeechend = () => {
        // Client stopped speaking — finalise the session so the mic doesn't
        // re-hear TTS echo / ambient sound as another "utterance".
        if (!userStoppedRef) {
          userStoppedRef = true;
          try { rec.stop(); } catch (_) { /* ignore */ }
        }
      };
      rec.onresult = (event) => {
        // Rebuild the transcript idempotently from ALL results each event.
        // Dedupe CONSECUTIVE IDENTICAL final chunks — real Chrome often
        // re-hears its own TTS echo (or the user's audio bounced from the
        // speakers) and delivers the exact same phrase as a second final
        // chunk. Keeping only one produces "hello" instead of "hello hello".
        // Distinct chunks (e.g. long statement broken into "I have three"
        // then "children") are preserved.
        const finalChunks = [];
        let interimTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          const chunk = event.results[i][0].transcript.trim();
          if (!chunk) continue;
          if (event.results[i].isFinal) {
            const last = finalChunks[finalChunks.length - 1];
            if (!last || last.toLowerCase() !== chunk.toLowerCase()) {
              finalChunks.push(chunk);
            }
          } else {
            interimTranscript += (interimTranscript ? ' ' : '') + chunk;
          }
        }
        const finalTranscript = finalChunks.join(' ');
        const dictated = (finalTranscript + ' ' + interimTranscript).trim();
        interimRef.current = dictated;
        onChange([interimSeedRef.current, dictated].filter(Boolean).join(' '));
      };
      rec.onerror = (e) => {
        setError(getSttErrorMessage(e?.error));
        recognitionRef.current = null;
        setIsRecording(false);
      };
      rec.onend = () => {
        // Auto-punctuate once the mic actually stops, so the psychologist sees the raw stream
        // during dictation and a clean sentence after they finish.
        const dictated = interimRef.current?.trim();
        if (dictated) onChange([interimSeedRef.current, autoPunctuate(dictated)].filter(Boolean).join(' '));
        setIsRecording(false);
        recognitionRef.current = null;
      };
      recognitionRef.current = rec;
      rec.start();
    } catch (e) {
      recognitionRef.current = null;
      setError(getSttErrorMessage());
      setIsRecording(false);
    }
  };
  const stopRecording = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
    }
    recognitionRef.current = null;
    setIsRecording(false);
  };

  // Cleanup on unmount
  useEffect(() => () => {
    stopRecording();
    stopPlayback();
  }, []);

  // Auto-play the question when the component mounts (once per question).
  // Waits a beat so the voice list is populated in the parent's dropdown.
  // StrictMode-safe: refs are set INSIDE the timer so the effect cleanup
  // (which runs before the second mount pass under StrictMode) can't leave
  // us in a "already-played" state while the timer was killed.
  useEffect(() => {
    if (!autoPlay || autoPlayedRef.current || !ttsSupported || !question) return;
    const t = setTimeout(() => {
      if (autoPlayedRef.current) return;
      autoPlayedRef.current = true;
      autoPlayInFlightRef.current = true;
      playQuestion();
    }, 250);
    return () => clearTimeout(t);
  }, [autoPlay, question, voices.length]);

  const clearAnswer = () => {
    interimRef.current = '';
    onChange('');
  };

  const hasAnswer = Boolean((value || '').trim().length);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4" data-testid={testId || 'voice-question'}>
      {/* Question row */}
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={isPlaying ? stopPlayback : playQuestion}
          disabled={!ttsSupported}
          className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 disabled:opacity-40"
          aria-label={isPlaying ? 'Stop playback' : 'Play question'}
          data-testid="vq-play-btn"
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <div className="flex-1">
          <p className="text-base text-gray-900 leading-snug">{question}</p>
          {helperText && (
            <p className="text-xs text-gray-500 leading-relaxed mt-1" data-testid="vq-helper-text">
              {helperText}
            </p>
          )}
        </div>
      </div>

      {/* Answer display */}
      {typeMode ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
          data-testid="vq-textarea"
        />
      ) : (
        <div
          className={`min-h-[64px] rounded-md border px-3 py-2 text-sm ${
            hasAnswer ? 'bg-gray-50 text-gray-900' : 'bg-gray-50 text-gray-400 italic'
          }`}
          data-testid="vq-transcript"
        >
          {hasAnswer ? (displayValue || value) : placeholder}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center flex-wrap gap-2">
        {!typeMode && (
          <Button
            type="button"
            variant={isRecording ? 'destructive' : 'default'}
            onClick={isRecording ? stopRecording : startRecording}
            className={isRecording ? 'animate-pulse' : 'bg-indigo-600 hover:bg-indigo-700'}
            data-testid="vq-record-btn"
          >
            {isRecording ? (
              <><MicOff className="w-4 h-4 mr-2" /> Stop recording</>
            ) : (
              <><Mic className="w-4 h-4 mr-2" /> {hasAnswer ? 'Record more' : 'Record answer'}</>
            )}
          </Button>
        )}
        {isRecording && !typeMode && <ListeningIndicator />}

        {allowType && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setTypeMode((v) => !v)}
            data-testid="vq-type-toggle"
          >
            <Keyboard className="w-4 h-4 mr-2" />
            {typeMode ? 'Voice instead' : 'Type instead'}
          </Button>
        )}

        {hasAnswer && (
          <Button
            type="button"
            variant="outline"
            onClick={clearAnswer}
            data-testid="vq-clear-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Re-record
          </Button>
        )}

        <div className="ml-auto flex items-center gap-2">
          {onSkip && (
            <Button type="button" variant="ghost" onClick={onSkip} data-testid="vq-skip-btn">
              Skip
            </Button>
          )}
          <Button
            type="button"
            onClick={() => {
              // Ensure the mic and any TTS in flight are stopped so the
              // NEXT question's mount can cleanly auto-play + auto-record
              // without racing an in-flight recognition instance.
              try { stopRecording(); } catch (_) {}
              try { stopPlayback(); } catch (_) {}
              if (typeof onConfirm === 'function') onConfirm();
            }}
            disabled={!hasAnswer}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="vq-confirm-btn"
          >
            <Check className="w-4 h-4 mr-2" /> Confirm & Next
          </Button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600" data-testid="vq-error">{error}</p>
      )}
      {!sttSupported && !typeMode && (
        <p className="text-xs text-amber-700">
          {availability.message}
        </p>
      )}
    </div>
  );
};

export default VoiceQuestion;
